import React, { useState, useEffect } from 'react';
import { FaLock } from 'react-icons/fa';
import { useNavigate, useLocation } from 'react-router-dom';
import MusicPage from './MusicPage';
import PartyMembers from './PartyMembers';
import PaymentPlan from './PaymentPlan';
import MainLogo from '../AllImages/GetItRight.png';
import Support from './Support';
import ItineraryPage from './ItineraryPage';
import AuditionForm from './AuditionForm';
import { getAuth, signOut } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase';
import DownPay from '../MainPage/DownPaymentPage';
import SettingsPage from './SettingsPage';
import MusicCompetiton from './MusicCompetition';
import OnlineRehearsal from './OnlineRehearsal';

const DashBoard = () => {
  const auth = getAuth();
  const user = auth.currentUser;
  const uid = user?.uid;
  const name = user?.displayName || user?.email || "Not Logged in";

  const [activeTab, setActiveTab] = useState('audition');
  const [showDropdown, setShowDropdown] = useState(false);
  const [studentAccepted, setStudentAccepted] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchStudentAccepted = async () => {
      if (uid) {
        try {
          const docRef = doc(db, "reservations", uid);
          const docSnap = await getDoc(docRef);
          if (docSnap.exists()) {
            const data = docSnap.data();
            setStudentAccepted(data.studentAccepted);
            console.log("Fetched studentAccepted:", data.studentAccepted);
          } else {
            console.log("No reservation found for uid:", uid);
          }
        } catch (error) {
          console.error("Error fetching studentAccepted:", error);
        }
      }
    };
    fetchStudentAccepted();
  }, [uid]);

  // Add music-competition and online-rehearsals to lockedTabs as needed.
  const lockedTabs = {
    participants: !studentAccepted,
    itinerary: !studentAccepted,
    payment: !studentAccepted,
    music: !studentAccepted,
    "music-competition": !studentAccepted,
    "online-rehearsals": !studentAccepted, // New tab: Online Rehearsals
    // Audition, Support, Settings remain accessible.
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error("Error signing out:", error);
    }
  };

  const navItemClass = (tabKey) => {
    const isActive = activeTab === tabKey;
    const isLocked = lockedTabs[tabKey];
    return [
      "transition-colors",
      "flex items-center space-x-1",
      isActive ? "text-yellow-400" : "text-gray-300 hover:text-yellow-400",
      isLocked && "line-through cursor-not-allowed opacity-50 hover:text-gray-300",
    ]
      .filter(Boolean)
      .join(" ");
  };

  const renderNavItem = (tabKey, label) => (
    <button
      key={tabKey}
      onClick={() => {
        if (!lockedTabs[tabKey]) {
          setActiveTab(tabKey);
          setShowDropdown(false);
        }
      }}
      className={navItemClass(tabKey)}
    >
      <span>{label}</span>
      {lockedTabs[tabKey] && <FaLock className="text-gray-400" />}
    </button>
  );

  return (
    <div className="min-h-screen bg-gray-800 text-gray-100 flex flex-col">
      <header className="bg-gray-900 border-b border-gray-700 relative">
        <div className="flex items-center justify-between px-6 py-3">
          <div className="flex items-center space-x-6">
            <img src={MainLogo} alt="Main Logo" className="h-10 w-auto" />
            <nav className="hidden md:flex space-x-6">
              {renderNavItem('audition', 'Audition')}
              {renderNavItem('participants', 'Participants')}
              {renderNavItem('itinerary', 'Itinerary')}
              {renderNavItem('payment', 'Payment Plan')}
              {renderNavItem('music', 'Music')}
              {renderNavItem('music-competition', 'Music Competition')}
              {renderNavItem('online-rehearsals', 'Online Rehearsals')}
              <button
                onClick={() => setActiveTab('support')}
                className={navItemClass('support')}
              >
                Support
              </button>
            </nav>
          </div>
          <div
            className="cursor-pointer hover:text-yellow-400 transition-colors relative"
            onClick={() => setShowDropdown(!showDropdown)}
          >
            <div className="flex items-center space-x-1">
              <span>{name || "Not Logged In"}</span>
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
              </svg>
            </div>
            {showDropdown && (
              <div className="absolute right-0 mt-2 w-40 bg-gray-800 border border-gray-700 rounded shadow-lg z-10 text-gray-200">
                <div className="block md:hidden">
                  {renderNavItem('audition', 'Audition')}
                  {renderNavItem('participants', 'Participants')}
                  {renderNavItem('itinerary', 'Itinerary')}
                  {renderNavItem('payment', 'Payment Plan')}
                  {renderNavItem('music', 'Music')}
                  {renderNavItem('music-competition', 'Music Competition')}
                  {renderNavItem('online-rehearsals', 'Online Rehearsals')}
                  <button
                    onClick={() => {
                      setActiveTab('support');
                      setShowDropdown(false);
                    }}
                    className={navItemClass('support')}
                  >
                    Support
                  </button>
                  <hr className="my-2 border-gray-700" />
                </div>
                <button
                  className="block w-full text-left px-4 py-2 hover:bg-gray-700"
                  onClick={() => {
                    setActiveTab('settings');
                    setShowDropdown(false);
                  }}
                >
                   Settings
                </button>
                <button
                  className="block w-full text-left px-4 py-2 hover:bg-gray-700"
                  onClick={handleSignOut}
                >
                 Sign out
                </button>
              </div>
            )}
          </div>
        </div>
      </header>

      <main className="flex-grow p-6">
        {activeTab === 'audition' &&
          (studentAccepted ? (
            <DownPay uid={uid} name={name} />
          ) : (
            <AuditionForm uid={uid} name={name} />
          ))
        }
        {activeTab === 'participants' && <PartyMembers uid={uid} name={name} />}
        {activeTab === 'itinerary' && <ItineraryPage uid={uid} name={name} />}
        {activeTab === 'payment' && <PaymentPlan uid={uid} name={name} />}
        {activeTab === 'music' && <MusicPage uid={uid} name={name} />}
        {activeTab === 'music-competition' && <MusicCompetiton uid={uid} name={name} />}
        {activeTab === 'online-rehearsals' && <OnlineRehearsal uid={uid} name={name} />}
        {activeTab === 'support' && <Support uid={uid} name={name} />}
        {activeTab === 'settings' && <SettingsPage uid={uid} name={name} />}
      </main>
    </div>
  );
};

export default DashBoard;
